import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import youCaterLogo from "../../assets/images/youCaterLogo.png";
import PhBuildingsSvg from "../../assets/svg-fc/PhBuildingsSvg";
import PhCallBellSvg from "../../assets/svg-fc/PhCallBellSvg";
import PhChatCircleTextSvg from "../../assets/svg-fc/PhChatCircleTextSvg";
import PhQuestionSvg from "../../assets/svg-fc/PhQuestionSvg";
import PhUsersThreeSvg from "../../assets/svg-fc/PhUsersThreeSvg";
import Header from "../../components/core-components/header/Header";
import SideNav, { iSideNavItem, sideNavActiveKey } from "../../components/reusable-components/side-nav/SideNav";
import {
	headerTrail,
	navKeyForHref,
	navPathname,
} from "../../components/reusable-components/side-nav/headerTrail";
import { iPageCrumb, PageCrumbContext } from "../../context/PageCrumbContext";
import { REACT_APP_CUSTOMER_PORTAL_URL } from "../../constants/envConstants";
import { useUserContext } from "../../context/UserContext";
import { useTermsModalContext } from "../../context/TermsModalContext";
import TermsGate from "../../components/pages-components/terms/TermsGate";
import TermsNoticeBanner from "../../components/pages-components/terms/TermsNoticeBanner";
import useUserInfo from "../../hooks/reduxHelpersHooks/useUserInfo";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { ROUTE_PATHS, ROUTE_QUERY_CONSTANTS } from "../../routes";
import IsMobile from "../../hooks/isMobile";
import { UI_ZOOM } from "../../constants/designTokens";
import "./DashboardLayout.css";

interface iDashboardLayoutProps {
	getInnerPagePath: string | boolean;
	getB2BEventDetailPath: string | boolean;
	hideHeader: boolean;
}

const DashboardLayout: FC<iDashboardLayoutProps> = ({
	getInnerPagePath,
	getB2BEventDetailPath,
	hideHeader,
}) => {
	const dispatch = useAppDispatch();
	const location = useLocation();
	const isMobile = IsMobile();
	const { userDetails } = useUserInfo();
	const { logout } = useUserContext();
	const { showNoticeBanner, openNoticeViewer, openReadOnlyViewer, remindSuperAdmin } =
		useTermsModalContext();
	const { current, cooldownExpiresAt, reminderRecipientName, isReminding } = useAppSelector(
		(state) => state.terms,
	);
	// Per vendor, not per team member — a colleague's reminder disables it for all.
	const isReminderOnCooldown = !!cooldownExpiresAt && new Date(cooldownExpiresAt) > new Date();
	const termsBannerRef = useRef<HTMLDivElement | null>(null);
	const layoutRef = useRef<HTMLDivElement | null>(null);

	/*
	 * Several pages size themselves against the viewport rather than their grid
	 * row — `chats.scss` uses `calc(100dvh - $app_header_height)`, which assumes
	 * the header is the only thing above them. Publishing the banner's real height
	 * lets those rules subtract it instead of overflowing the page.
	 *
	 * Measured rather than hardcoded because the banner wraps to two lines on
	 * narrow viewports.
	 */
	useEffect(() => {
		const root = document.documentElement;
		const banner = termsBannerRef.current;

		if (!banner) {
			root.style.setProperty("--terms-banner-height", "0px");
			return;
		}

		const publishHeight = () => {
			const { height } = banner.getBoundingClientRect();
			root.style.setProperty("--terms-banner-height", `${Math.ceil(height)}px`);
		};

		publishHeight();

		const observer = new ResizeObserver(publishHeight);
		observer.observe(banner);

		return () => {
			observer.disconnect();
			root.style.setProperty("--terms-banner-height", "0px");
		};
	}, [showNoticeBanner, current?.effectiveDate]);
	const notificationCount = useAppSelector((state) => state.notifications.notificationCount);
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [notificationAnchorEl, setNotificationAnchorEl] = React.useState<null | HTMLElement>(null);
	const notificationOpen = Boolean(notificationAnchorEl);
	const avataOpen = Boolean(anchorEl);
	const [sideNavOpen, setSideNavOpen] = useState(false);
	/** Below the desktop width the nav is a drawer opened from the header. */
	const compact = useMediaQuery("(max-width:1023.98px)");
	/** The nav and header at Maktab's 112% interface size, as on admin (from 600px, like Maktab). */
	const shellZoom = useMediaQuery("(min-width:600px)") ? UI_ZOOM : 1;
	/** A detail page's own name and list for the header breadcrumb (usePageCrumb). */
	const [pageCrumb, setPageCrumb] = useState<iPageCrumb | null>(null);

	const closeSideNav = useCallback(() => {
		setSideNavOpen(false);
	}, []);
	useEffect(() => {
		closeSideNav();
	}, [location.pathname, closeSideNav]);
	const notificationBadgeCount = useAppSelector((state) => state.notifications.badgeCount);

	const notificationHandleClick = (event: React.MouseEvent<HTMLElement>) => {
		setNotificationAnchorEl(event.currentTarget);
	};

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleOnClickLogoutButton = useCallback(() => {
		logout(ROUTE_PATHS.LOGIN);
	}, [dispatch]);

	const notificationHandleClose = () => {
		setNotificationAnchorEl(null);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const navItems: iSideNavItem[] = [
		{
			key: "messages",
			label: "Messages",
			icon: <PhChatCircleTextSvg />,
			href: ROUTE_PATHS.CHATS,
		},
		{
			key: "consumer",
			label: "Consumer",
			icon: <PhUsersThreeSvg />,
			children: [
				{
					key: "ai-direct-request",
					label: "AI Direct Request",
					href: ROUTE_PATHS.AI_DIRECT_REQUEST,
					query: ROUTE_QUERY_CONSTANTS.AiDirectRequest,
					dot: notificationCount.aiDirectRequestNotificationCount > 0,
				},
				{
					key: "direct-request",
					label: "User Direct Request",
					href: ROUTE_PATHS.DIRECTORY_REQUEST_EVENTS,
					query: ROUTE_QUERY_CONSTANTS.DirectRequests,
					dot: notificationCount.directRequestNotificationCount > 0,
				},
				{
					key: "in-progress",
					label: "In Progress",
					href: ROUTE_PATHS.IN_PROGRESS_EVENTS,
					query: ROUTE_QUERY_CONSTANTS.InProgress,
					dot: notificationCount.inProgressNotificationCount > 0,
				},
				{
					key: "approved",
					label: "Approved Events",
					href: ROUTE_PATHS.APPROVED_EVENTS,
					query: ROUTE_QUERY_CONSTANTS.Approved,
					dot: notificationCount.approvedNotificationCount > 0,
				},
				{
					key: "declined",
					label: "Declined Events",
					href: ROUTE_PATHS.EVENT_HISTORY_DECLINED,
					query: ROUTE_QUERY_CONSTANTS.Declined,
				},
				{
					key: "completed",
					label: "Completed Events",
					href: ROUTE_PATHS.EVENT_HISTORY_COMPLETED,
					query: ROUTE_QUERY_CONSTANTS.Completed,
				},
			],
		},
		{
			key: "corporate",
			label: "Corporate",
			icon: <PhBuildingsSvg />,
			children: [
				{
					key: "corporate-active",
					label: "Corporate RFPs",
					href: ROUTE_PATHS.B2B.ACTIVE_EVENTS,
					query: ROUTE_QUERY_CONSTANTS.CorporateActiveEvents,
					dot: notificationCount.corporateEventRequestNotificationCount > 0,
				},
				{
					key: "corporate-approved",
					label: "Approved Events",
					href: ROUTE_PATHS.B2B.APPROVED_EVENTS,
					query: ROUTE_QUERY_CONSTANTS.CorporateApprovedEvents,
					dot: notificationCount.corporateEventApprovedNotificationCount > 0,
				},
				{
					key: "corporate-declined",
					label: "Declined Events",
					href: ROUTE_PATHS.B2B.DECLINED_EVENTS,
					query: ROUTE_QUERY_CONSTANTS.CorporateDeclinedEvents,
				},
				{
					key: "corporate-completed",
					label: "Completed Events",
					href: ROUTE_PATHS.B2B.COMPLETED_EVENTS,
					query: ROUTE_QUERY_CONSTANTS.CorporateCompletedEvents,
				},
			],
		},
		{
			key: "pre-paid",
			label: "Pre-Paid Requests",
			icon: <PhCallBellSvg />,
			href: ROUTE_PATHS.PRE_PAID_REQUESTS,
			query: ROUTE_QUERY_CONSTANTS.PrePaidRequests,
			dot: notificationCount.prepaidRequestNotificationCount > 0,
		},
		{
			key: "help",
			label: "Help",
			icon: <PhQuestionSvg />,
			dividerBefore: true,
			children: [
				{
					key: "faqs",
					label: "FAQs",
					onClick: () => window.open(`${REACT_APP_CUSTOMER_PORTAL_URL}/en/partner/#partner-faqs`, "_blank"),
				},
				{
					key: "terms",
					label: "Terms & Conditions",
					// In-portal, read-only. Never a new tab, never a download.
					onClick: openReadOnlyViewer,
				},
			],
		},
	];

	// A detail page's list (from usePageCrumb) is the nav entry it belongs to; otherwise the route, or `?type=`.
	const activeNavKey =
		navKeyForHref(navItems, pageCrumb?.parentHref) ||
		sideNavActiveKey(navItems, navPathname(location.pathname), location.search);
	const navHasDot = navItems.some((item) => item.dot || item.children?.some((child) => child.dot));

	const showHeader = !((getInnerPagePath || getB2BEventDetailPath || hideHeader) && isMobile);

	/*
	 * The chat pages size themselves against the header (`$app_header_height`), whose height changes with the
	 * screen width and its zoom — publish the real one, as for the T&Cs banner.
	 */
	useEffect(() => {
		const root = document.documentElement;
		const header = layoutRef.current?.querySelector<HTMLElement>(":scope > .header");
		if (!header) {
			root.style.removeProperty("--app-header-height");
			return;
		}
		const publishHeight = () => {
			root.style.setProperty("--app-header-height", `${Math.ceil(header.getBoundingClientRect().height)}px`);
		};
		publishHeight();
		const observer = new ResizeObserver(publishHeight);
		observer.observe(header);
		return () => {
			observer.disconnect();
			root.style.removeProperty("--app-header-height");
		};
	}, [showHeader, compact, shellZoom]);

	return (
		<Box ref={layoutRef} className={`dashboardParentLayout ${compact ? "compact" : ""}`}>
			<SideNav
				items={navItems}
				activeKey={activeNavKey}
				compact={compact}
				drawerOpen={sideNavOpen}
				onCloseDrawer={closeSideNav}
				logoSrc={youCaterLogo}
				zoom={shellZoom}
			/>
			{!showHeader ? (
				<></>
			) : (
				<Header
					isNotificationReceived={notificationBadgeCount > 0}
					notificationOpen={notificationOpen}
					notificationHandleClick={notificationHandleClick}
					notificationAnchorEl={notificationAnchorEl}
					notificationHandleClose={notificationHandleClose}
					avataOpen={avataOpen}
					handleClose={handleClose}
					handleClick={handleClick}
					anchorEl={anchorEl}
					avatarName={userDetails != null ? `${userDetails?.firstName} ${userDetails?.lastName || ""}` : ""}
					avatarEmail={userDetails?.email !== undefined ? userDetails?.email : ""}
					avatarSrc={userDetails?.logo || undefined}
					buttonLabel="Logout"
					logoutHandler={handleOnClickLogoutButton}
					compact={compact}
					onOpenMenu={() => setSideNavOpen(true)}
					notificationBadge={navHasDot}
					zoom={shellZoom}
					trail={headerTrail(navItems, activeNavKey, pageCrumb)}
				/>
			)}
			{/* Occupies layout space above page content rather than floating over it. */}
			{showNoticeBanner && current?.effectiveDate && (
				<Box className="termsNoticeBannerSlot" ref={termsBannerRef}>
					<TermsNoticeBanner
						effectiveDate={current.effectiveDate}
						canAccept={!!current.canAccept}
						isReminderOnCooldown={isReminderOnCooldown}
						isReminding={isReminding}
						reminderRecipientName={reminderRecipientName}
						onReviewTerms={openNoticeViewer}
						onRemindSuperAdmin={remindSuperAdmin}
					/>
				</Box>
			)}
			<PageCrumbContext.Provider value={setPageCrumb}>
				<Outlet />
			</PageCrumbContext.Provider>
			{/* Single mount point for the blocking modal and the document viewer. */}
			<TermsGate />
		</Box>
	);
};
export default DashboardLayout;
